import { CognitoUser } from "amazon-cognito-identity-js";
import {
	Backdrop,
	Box,
	CircularProgress,
	createTheme,
	CssBaseline,
	Link,
	Theme,
	ThemeProvider,
	Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getCurrentUser, signInWithRedirect } from "aws-amplify/auth";
import { ConsoleLogger, Hub } from "aws-amplify/utils";
import { Amplify } from "aws-amplify";
import { signOut } from "aws-amplify/auth";
import React, { useCallback, useState, useEffect } from "react";
import { BreadcrumbsRoute } from "react-router-breadcrumbs-hoc";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import SideBar from "./components/SideBar/SideBar";
import UserProfile from "./components/SideBar/UserProfile";
import AdminPage from "./components/Admin/AdminPage";
import ResidentsFunctional from "./components/Admin/ViewResidents";
import Header from "./components/Header/Header";
import LogPage from "./components/Admin/Logpage";
import config from "./amplifyconfiguration.json";
import IntakeSearch from "./components/Intake/IntakeSearch";
import UpdateThere from "./components/Detail/Update";
import SessionHandler from "./SessionHandler";
import AddStaff from "./components/Detail/AddStaff";
import ResidentExportDirectory from "./components/Page/ResidentDirectory/ResidentExportDirectory";
Amplify.configure(config);

ConsoleLogger.LOG_LEVEL = "VERBOSE";
const logger = new ConsoleLogger("App");

declare module "@mui/styles/defaultTheme" {
	// eslint-disable-next-line @typescript-eslint/no-empty-interface
	// tslint:disable-next-line: no-empty-interface
	interface DefaultTheme extends Theme {}
}
const defaultTheme = createTheme();
defaultTheme.palette.background.default = "#fff";
defaultTheme.palette.primary.main = "rgb(20,59,89)";
defaultTheme.palette.primary.dark = defaultTheme.palette.primary.main; // header color
defaultTheme.palette.primary.light = "rgb(101, 125, 255)";
defaultTheme.palette.success.main = "rgb(58,152,75)"; // sidebar color
defaultTheme.palette.warning.main = "rgb(255, 95, 0)";
defaultTheme.palette.info.main = "rgb(135,135,135)";

// let defaultTheme = createTheme({
//     palette: {
//         background: {
//             default: '#fff'
//         },
//         primary: {
//             main: 'rgb(20,59,90)',
//             light: 'rgb(101, 125, 255)'
//         },
//         success: {
//             main: 'rgb(58,152,75)' // the color for sidebar
//         },
//         warning: {
//             main:  'rgb(255, 95, 0)'
//         },
//         info: {
//             main: 'rgb(135,135,135)'
//         }
//     }
// })

// defaultTheme = createTheme(defaultTheme, {
//     palette: {
//         primary: {
//             dark: defaultTheme.palette.primary.main // for header dark blue color
//         },
//     },
// });

const DRAWER_WIDTH = 270;

const theme = {
	...defaultTheme,
	components: {
		MuiButton: {
			styleOverrides: {
				containedPrimary: {
					backgroundColor: defaultTheme.palette.background.default,
					color: "rgba(0, 0, 0, 0.87)",
				},
			},
		},
		MuiTypography: {
			styleOverrides: {
				h1: {
					fontSize: "2rem",
					fontWeight: 900,
					textTransform: "uppercase",
					color: defaultTheme.palette.success.main,
				},
				h2: {
					fontSize: "1.75rem",
					fontWeight: 900,
					textTransform: "uppercase",
					color: defaultTheme.palette.primary.dark,
				},
				h3: {
					fontSize: "1.5rem",
					fontWeight: 900,
					color: defaultTheme.palette.primary.dark,
				},
				h4: {
					fontSize: "1.25rem",
					fontWeight: 900,
					color: defaultTheme.palette.primary.dark,
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				root: {
					width: DRAWER_WIDTH,
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				colorPrimary: {
					backgroundColor: defaultTheme.palette.primary.dark, // background for header
				},
				root: {
					width: `calc(100% - ${DRAWER_WIDTH}px)`,
					height: 200,
				},
			},
		},
		MuiCardHeader: {
			styleOverrides: {
				title: {
					color: "#fff",
					fontSize: "1.1rem",
					fontWeight: 600,
				},
				subheader: {
					color: "#fff",
					fontSize: ".8rem",
					fontWeight: 400,
				},
				root: {
					paddingTop: "16px",
					padEnd: "16px",
					padStart: "16px",
					paddingBottom: "2px",
				},
			},
		},
		MuiCardActions: {
			styleOverrides: {
				root: {
					padding: "0px",
				},
			},
		},
	},
} as Theme;

// Full-width header on mobile devices
if (theme.components?.MuiAppBar?.styleOverrides?.root) {
	theme.components.MuiAppBar.styleOverrides.root = {
		[theme?.breakpoints?.up("md")]: {
			width: "100% !important",
		},
	};
}

const useStyles = makeStyles((pTheme) => ({
	container: {
		[pTheme?.breakpoints?.down("md")]: {
			width: "100% !important",
			marginLeft: "0",
			borderRadius: 0,
		},
		paddingTop: 20,
		paddingLeft: "4%",
		paddingRight: "4%",
		marginTop: 35,
		width: `calc(95% - ${DRAWER_WIDTH}px)`,
		position: "relative",
		zIndex: 1300,
		backgroundColor: "#fff",
		top: -170,
		marginLeft: "2.5%",
		borderRadius: 12,
	},
}));

function Copyright() {
	return (
		<Typography variant="body2" color="textSecondary" align="center">
			{"Copyright © "}
			<Link color="inherit" href="https://material-ui.com/">
				Erickson Living
			</Link>{" "}
			{new Date().getFullYear()}
			{"."}
		</Typography>
	);
}

const AppMain: React.FC = (props) => {
	const [user, setUser] = useState<CognitoUser>();

	const logout = useCallback(() => signOut(), []);

	Hub.listen("auth", ({ payload: { event } }) => {
		switch (event) {
			case "customOAuthState":
				const targetUrl = localStorage.getItem("targetUrl") || "/";
				localStorage.removeItem("targetUrl");
				window.location.pathname = targetUrl;
		}
	});

	useEffect(() => {
		(async () => {
			try {
				await new Promise((r) => setTimeout(r, 3000));
				const authuser: any = await getCurrentUser();
				logger.verbose("authUser", authuser);
				setUser(authuser);
				localStorage.setItem("authuser", authuser?.username);
				localStorage.setItem("loginTime", new Date().toLocaleString());
			} catch (err) {
				logger.error("Err", err);
				localStorage.setItem("targetUrl", window.location.pathname);
				signInWithRedirect({
					provider: {
						custom: "Erickson",
					},
				});
			}
		})();
	}, []);

	const breadcrumbRoutes = [
		{ path: "/", breadcrumb: "Home" },
		{ path: "/admin", breadcrumb: "Admin Console" },
	] as BreadcrumbsRoute[];
	const [menuVisible, setMenuVisible] = useState<boolean>(false);
	const handleMenuClick = () => {
		setMenuVisible(!menuVisible);
	};
	const App = () => {
		const classes = useStyles();
		return (
			<Router>
				<SessionHandler onLogOut={logout} />
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<Header
						user={user}
						onLogOut={logout}
						onMenuClick={handleMenuClick}
						breadcrumbRoutes={breadcrumbRoutes}
					/>
					<main id="mainContainer" className={classes.container}>
						{user && (
							<Switch>
								<Route exact path="/" component={IntakeSearch} />
								<Route exact path="/profile" component={UserProfile} />
								<Route exact path="/log" component={LogPage} />
								<Route exact path="/admin" component={AdminPage} />
								<Route exact path="/edit/:pid" component={UpdateThere} />
								<Route exact path="/add" component={AddStaff} />
								<Route
									exact
									path="/undergoingDevelopement"
									component={ResidentsFunctional}
								/>
								{/* <Route exact path = '/report' component={ResidentsFunctional} /> */}
								<Route
									exact
									path="/directory"
									component={ResidentExportDirectory}
								/>
							</Switch>
						)}

						<Box pt={1} mt={20}>
							<Copyright />
						</Box>
					</main>

					<SideBar
						user={user}
						menuVisible={menuVisible}
						onLogout={logout}
						onNavigate={() => {
							setMenuVisible(false);
						}}
					/>
				</ThemeProvider>
			</Router>
		);
	};
	const LoadingScreen = () => {
		return (
			<React.Fragment>
				<img
					style={{
						position: "fixed",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						zIndex: -1,
					}}
					src="/ehr.svg"
				/>
				<Backdrop open={true} invisible={true}>
					<CircularProgress size={75} thickness={6} color="primary" />
				</Backdrop>
			</React.Fragment>
		);
	};

	return (
		<ThemeProvider theme={theme}>
			{user ? <App /> : <LoadingScreen />}
		</ThemeProvider>
	);
};
export default AppMain;
